import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pause, Play, Plus, RefreshCw, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { inputClass, labelClass } from "@/lib/constants";
import { Checkbox } from "@/components/ui/checkbox";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const dayFmt = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/London" });

export default function AdminSlots() {
  const [templates, setTemplates] = useState(null);
  const [slots, setSlots] = useState(null);
  const [weeks, setWeeks] = useState(null);
  const [form, setForm] = useState({ weekday: 4, type: "collection", start_time: "10:00", end_time: "16:00", default_capacity: 10 });
  const [weekEdits, setWeekEdits] = useState({});

  const load = async () => {
    const [t, s, w] = await Promise.all([
      api.get("/admin/slot-templates"),
      api.get("/admin/slots"),
      api.get("/admin/weeks"),
    ]);
    setTemplates(t.data);
    setSlots(s.data);
    setWeeks(w.data);
  };
  useEffect(() => { load(); }, []);

  const run = (fn, msg) => fn().then(() => { toast.success(msg); load(); }).catch((e) => toast.error(formatApiError(e)));

  const addTemplate = (e) => {
    e.preventDefault();
    run(() => api.post("/admin/slot-templates", form), "Template saved");
  };

  const generate = () => run(() => api.post("/admin/slots/generate", { weeks_ahead: 4 }), "Slots generated (existing slots untouched)");

  const setCapacity = (slot, value) => {
    const capacity = parseInt(value, 10);
    if (!capacity) return;
    run(() => api.put(`/admin/slots/${slot.id}`, { capacity }), "Capacity updated");
  };

  const saveWeek = (week) => {
    const edit = weekEdits[week.week_start_date] || {};
    run(
      () => api.put(`/admin/weeks/${week.week_start_date}`, {
        cutoff_override_local: edit.cutoff_override_local || null,
        closed_early: edit.closed_early ?? week.closed_early,
        note: edit.note ?? week.note,
        blackout_dates: (edit.blackout_dates ?? (week.blackout_dates || []).join(", ")).split(",").map((x) => x.trim()).filter(Boolean),
      }),
      "Week override saved"
    );
  };

  const weekEdit = (key) => weekEdits[key] || {};
  const setWeekEdit = (key, patch) => setWeekEdits((m) => ({ ...m, [key]: { ...m[key], ...patch } }));

  return (
    <div data-testid="admin-slots-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-bold text-cream-text">Delivery &amp; collection slots</h1>
          <p className="mt-1 text-sm text-cream-muted">Weekly deadline: Wednesday 6:00pm. Slots generate from recurring templates.</p>
        </div>
        <button onClick={generate} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco hover:bg-gold-hover" data-testid="generate-slots-button">
          <RefreshCw className="h-4 w-4" aria-hidden="true" /> Generate next 4 weeks
        </button>
      </div>

      <section className="mt-6 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="templates-section">
        <h2 className="font-serif text-lg font-semibold text-cream-text">Recurring templates</h2>
        <div className="mt-4 space-y-2" data-testid="templates-list">
          {(templates || []).map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-choco-line bg-choco p-4" data-testid={`template-row-${t.id}`}>
              <p className="text-sm text-cream-text">
                <span className="font-semibold">{WEEKDAYS[t.weekday]}s</span> · {t.type} · {t.start_time}–{t.end_time} · capacity {t.default_capacity}
                {!t.active && <span className="ml-2 text-xs text-cream-muted">(inactive)</span>}
              </p>
              <div className="flex gap-2">
                <button onClick={() => run(() => api.put(`/admin/slot-templates/${t.id}`, { ...t, active: !t.active }), "Template updated")} className={`rounded-full px-4 py-1.5 text-xs font-bold ${t.active ? "bg-gold/20 text-gold" : "border border-choco-line text-cream-muted"}`} data-testid={`template-toggle-${t.id}`}>
                  {t.active ? "Active" : "Inactive"}
                </button>
                <button onClick={() => window.confirm("Delete this template? Existing slots remain.") && run(() => api.delete(`/admin/slot-templates/${t.id}`), "Template deleted")} aria-label="Delete template" className="rounded-full p-2 text-cream-muted hover:text-red-400" data-testid={`template-delete-${t.id}`}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={addTemplate} className="mt-5 flex flex-wrap items-end gap-3 border-t border-choco-line pt-5" data-testid="new-template-form">
          <div>
            <label htmlFor="t-day" className={labelClass}>Day</label>
            <select id="t-day" value={form.weekday} onChange={(e) => setForm((f) => ({ ...f, weekday: parseInt(e.target.value, 10) }))} className={inputClass} data-testid="template-weekday-select">
              {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="t-type" className={labelClass}>Type</label>
            <select id="t-type" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className={inputClass} data-testid="template-type-select">
              <option value="collection">Collection</option>
              <option value="delivery">Delivery</option>
            </select>
          </div>
          <div>
            <label htmlFor="t-start" className={labelClass}>Start</label>
            <input id="t-start" type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} className={inputClass} data-testid="template-start-input" />
          </div>
          <div>
            <label htmlFor="t-end" className={labelClass}>End</label>
            <input id="t-end" type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} className={inputClass} data-testid="template-end-input" />
          </div>
          <div>
            <label htmlFor="t-cap" className={labelClass}>Capacity</label>
            <input id="t-cap" type="number" min="1" value={form.default_capacity} onChange={(e) => setForm((f) => ({ ...f, default_capacity: parseInt(e.target.value || "1", 10) }))} className={`${inputClass} w-24`} data-testid="template-capacity-input" />
          </div>
          <button type="submit" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco hover:bg-gold-hover" data-testid="add-template-button">
            <Plus className="h-4 w-4" aria-hidden="true" /> Add template
          </button>
        </form>
      </section>

      <section className="mt-8 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="slots-section">
        <h2 className="font-serif text-lg font-semibold text-cream-text">Upcoming slots</h2>
        <div className="mt-4 space-y-2" data-testid="slots-list">
          {(slots || []).map((s) => (
            <div key={s.id} className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-4 ${s.active ? "border-choco-line bg-choco" : "border-amber-500/40 bg-amber-500/5"}`} data-testid={`slot-row-${s.id}`}>
              <p className="text-sm text-cream-text">
                <span className="font-semibold">{dayFmt.format(new Date(s.date + "T12:00:00"))}</span> · {s.type} · {s.start_time}–{s.end_time}
                {!s.active && <span className="ml-2 text-xs font-bold text-amber-400">PAUSED</span>}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-cream-muted" data-testid={`slot-capacity-${s.id}`}>{s.booked}/{s.capacity} booked · {s.remaining} left</span>
                <input type="number" min="1" defaultValue={s.capacity} aria-label="Capacity" className={`${inputClass} w-20 !py-1.5 text-xs`} onBlur={(e) => e.target.value !== String(s.capacity) && setCapacity(s, e.target.value)} data-testid={`slot-capacity-input-${s.id}`} />
                {s.active ? (
                  <button onClick={() => run(() => api.post(`/admin/slots/${s.id}/pause`), "Slot paused")} aria-label="Pause slot" className="rounded-full p-2 text-cream-muted hover:text-amber-400" data-testid={`slot-pause-${s.id}`}><Pause className="h-4 w-4" /></button>
                ) : (
                  <button onClick={() => run(() => api.post(`/admin/slots/${s.id}/resume`), "Slot reopened")} aria-label="Resume slot" className="rounded-full p-2 text-cream-muted hover:text-gold" data-testid={`slot-resume-${s.id}`}><Play className="h-4 w-4" /></button>
                )}
                <button onClick={() => window.confirm("Delete this slot?") && run(() => api.delete(`/admin/slots/${s.id}`), "Slot deleted")} aria-label="Delete slot" className="rounded-full p-2 text-cream-muted hover:text-red-400" data-testid={`slot-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-8 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="weeks-section">
        <h2 className="font-serif text-lg font-semibold text-cream-text">Weekly cut-offs, closures &amp; blackout dates</h2>
        <p className="mt-1 text-xs text-cream-muted">Default cut-off: Wednesday 6:00pm before each fulfilment week. Overrides apply to that week only.</p>
        <div className="mt-4 space-y-3" data-testid="weeks-list">
          {(weeks || []).map((w) => (
            <div key={w.week_start_date} className="rounded-2xl border border-choco-line bg-choco p-4" data-testid={`week-row-${w.week_start_date}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-cream-text">
                  Week of {dayFmt.format(new Date(w.week_start_date + "T12:00:00"))}
                  <span className="ml-2 text-xs font-normal text-cream-muted">{w.slot_count} slot(s)</span>
                  {w.closed_early && <span className="ml-2 rounded-full bg-red-500/20 px-2 py-0.5 text-[11px] font-bold text-red-300">CLOSED EARLY</span>}
                </p>
                <div className="flex items-center gap-2">
                  <Checkbox id={`closed-${w.week_start_date}`} checked={weekEdit(w.week_start_date).closed_early ?? w.closed_early} onCheckedChange={(v) => setWeekEdit(w.week_start_date, { closed_early: v === true })} className="border-choco-line data-[state=checked]:bg-gold data-[state=checked]:text-choco" data-testid={`week-closed-${w.week_start_date}`} />
                  <label htmlFor={`closed-${w.week_start_date}`} className="text-xs text-cream-muted">Close ordering early</label>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-cream-muted" htmlFor={`cutoff-${w.week_start_date}`}>Cut-off override (UK time, optional)</label>
                  <input id={`cutoff-${w.week_start_date}`} type="datetime-local" value={weekEdit(w.week_start_date).cutoff_override_local ?? ""} onChange={(e) => setWeekEdit(w.week_start_date, { cutoff_override_local: e.target.value })} className={inputClass} data-testid={`week-cutoff-${w.week_start_date}`} />
                  {w.cutoff_override_utc && <p className="mt-1 text-[11px] text-amber-400">Override active: {new Date(w.cutoff_override_utc).toLocaleString("en-GB", { timeZone: "Europe/London" })}</p>}
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-cream-muted" htmlFor={`blackout-${w.week_start_date}`}>Blackout dates (comma-separated)</label>
                  <input id={`blackout-${w.week_start_date}`} value={weekEdit(w.week_start_date).blackout_dates ?? (w.blackout_dates || []).join(", ")} onChange={(e) => setWeekEdit(w.week_start_date, { blackout_dates: e.target.value })} placeholder="2026-12-25, 2026-12-26" className={inputClass} data-testid={`week-blackouts-${w.week_start_date}`} />
                </div>
                <div className="flex items-end">
                  <button onClick={() => saveWeek(w)} className="rounded-full border border-choco-line px-5 py-2.5 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`week-save-${w.week_start_date}`}>
                    Save week
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
